import { viewAsProId } from '@/lib/viewAs';
import { ownerHubOrigin, ownerHubToken } from '@/lib/cell/ownerHub';

/**
 * HUB-025 (Tenean review 2026-10-06) · THE DESIGNER'S SIGN-IN, RENEWED. The studio gets this tab's
 * access token once, in the fragment; it lives about an hour and the studio cannot refresh it, so a
 * long design session ended in saves the door refused. Now, when the studio's sign-in runs out, it
 * asks THIS tab (its opener) for a fresh one. This tab answers only:
 *   · a window it opened itself (window.open below), and
 *   · from the Owner Hub's exact origin, and
 *   · with this tab's OWN current session — never while viewing as someone else —
 * and posts the reply to that window at that origin only. The studio half is image-os-web
 * src/designer/pro-token.ts (same two message types).
 */
export const DESIGNER_TOKEN_REQUEST = 'tenean:designer-token-request';
export const DESIGNER_TOKEN_REPLY = 'tenean:designer-token';

export function makeDesignerTokenBroker(deps: {
  opened: Set<unknown>;
  origin: string;
  currentToken: () => Promise<string | null>;
  viewing: () => string | null;
  /** This tab's window. HUB-025 (partly round): a studio this tab opened BEFORE it reloaded is no longer in `opened`
   *  (the set lives in memory), but that studio's `opener` is still this tab — readable across origins. */
  self?: unknown;
}) {
  const openedByMe = (src: Window): boolean => {
    if (deps.opened.has(src)) return true;
    if (!deps.self) return false;
    try { return src.opener != null && src.opener === deps.self; } catch { return false; }
  };
  return async (e: MessageEvent): Promise<boolean> => {
    if (e.origin !== deps.origin) return false;
    const src = e.source as Window | null;
    if (!src || !openedByMe(src)) return false;
    const d = e.data as { type?: unknown; nonce?: unknown } | null;
    if (!d || d.type !== DESIGNER_TOKEN_REQUEST || typeof d.nonce !== 'string' || !d.nonce) return false;
    if (deps.viewing()) return false;
    const at = await deps.currentToken();
    if (!at) return false;
    src.postMessage({ type: DESIGNER_TOKEN_REPLY, nonce: d.nonce, at }, deps.origin);
    return true;
  };
}

/** The studio windows this tab opened — the only ones it will hand a sign-in to (plus, after a reload, a window
 *  whose opener is this tab: see `self` above). */
export const openedStudios = new Set<unknown>();
let brokerListening = false;
/** Started at boot (main.tsx) and again by the Designer door — idempotent. */
export function listenForDesignerTokenRequests() {
  if (brokerListening || typeof window === 'undefined') return;
  // CELL (M28): the studio lives on this build's Owner Hub origin (production's OWNER_HUB_ORIGIN, or the AWS staging host);
  // with none set on the cell build there is no studio to answer, so the broker never listens.
  const origin = ownerHubOrigin();
  if (!origin) return;
  brokerListening = true;
  const broker = makeDesignerTokenBroker({
    opened: openedStudios,
    origin,
    // CELL (M28): this tab's own token — Supabase's in production (getSession refreshes an expired session before
    // answering, so it is fresh), Cognito's access token on the cell build (getCellSession refreshes the same way).
    currentToken: ownerHubToken,
    viewing: () => viewAsProId(),
    self: window,
  });
  window.addEventListener('message', (e) => { void broker(e); });
}

